"use client";

import type { BlockConfig } from "@/lib/registry/types";
import dynamic from "next/dynamic";
import type React from "react";

// Component cache to avoid unnecessary dynamic imports for the same components
const componentCache = new Map<string, React.ComponentType | null>();

/**
 * Enhanced error fallback component
 */
const ErrorFallback = ({ error }: { error?: string }) => (
  <div className="flex h-full w-full items-center justify-center bg-muted/20 border border-dashed border-muted-foreground/20 rounded-md">
    <div className="flex flex-col items-center gap-1 text-center p-2">
      <div className="text-sm text-muted-foreground">
        Component unavailable
      </div>
      {process.env.NODE_ENV === "development" && error && (
        <div className="text-xs text-muted-foreground/60 max-w-[200px] truncate">
          {error}
        </div>
      )}
    </div>
  </div>
);

/**
 * Loading fallback component
 */
const LoadingFallback = () => (
  <div className="h-full w-full animate-pulse bg-muted" />
);

/**
 * Load a component dynamically using Next.js's dynamic import
 * with enhanced caching and error handling
 */
export function getDynamicDemoComponent(
  component: BlockConfig,
  demoFile: string
) {
  if (!component.library || !component.name) {
    console.error("Missing library or name in component config");
    return () => <ErrorFallback error="Invalid component configuration" />;
  }

  // Create cache key for this component and demo file
  const cacheKey = `${component.library}/${component.name}/${demoFile}`;

  // Check if we already have this component in cache
  if (componentCache.has(cacheKey)) {
    const cachedComponent = componentCache.get(cacheKey);
    if (cachedComponent !== null) {
      return cachedComponent;
    }
    componentCache.set(cacheKey, null);
  }

  // Construct import path relative to public directory
  const importPath = `${component.library}/${component.name}/${demoFile}`;
  console.log("Loading demo component from:", importPath);

  // Use Next.js dynamic import to get the component with caching
  const DynamicComponent = dynamic(
    () =>
      Promise.race([
        import(
          /* webpackInclude: /\.tsx$/, webpackMode: "lazy" */ `../../../../../public/registry/src/${importPath}`
        )
          .then((mod) => {
            if (mod?.default) {
              componentCache.set(cacheKey, mod.default);
              return mod.default;
            }

            // Find the first exported component
            const componentExports = Object.values(mod);

            const componentExport = componentExports.find(
              (exp): exp is React.ComponentType =>
                typeof exp === "function" &&
                /^[A-Z]/.test((exp as any).name || "")
            );

            if (componentExport) {
              componentCache.set(cacheKey, componentExport);
              return componentExport;
            }

            if (componentExports.length > 0) {
              const fallbackExport = componentExports[0] as React.ComponentType;
              componentCache.set(cacheKey, fallbackExport);
              return fallbackExport;
            }

            console.error(`No component found in module: ${importPath}`);
            componentCache.set(cacheKey, null);
            return () => <ErrorFallback error="Component not found" />;
          })
          .catch((err) => {
            console.error("Error loading demo component:", err);
            componentCache.set(cacheKey, null);
            return () => <ErrorFallback />;
          }),
        new Promise<React.ComponentType<{}>>((_resolve, reject) =>
          setTimeout(() => {
            componentCache.set(cacheKey, null);
            reject(new Error("Component import timeout"));
          }, 10000)
        ),
      ]),
    {
      loading: LoadingFallback,
      // Use SSR false to avoid hydration issues with dynamic components
      ssr: false,
    }
  );

  return DynamicComponent;
}
